import React, { useState, useEffect, useRef } from "react";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";
import { Customer } from "../../../../types/customers.types";
import SimpleModal from "../../../../components/SimpleModal/SimpleModal";
import DocumentField, {
  DocumentInputRef,
} from "../DocumentField/DocumentField";
import BalanceField from "../BalanceField/BalanceField";
import { Container, Row } from "./CustomerFormModalStyles";
import { Effect } from "effector";

interface Props {
  customer?: Customer; // Se tiver, é edição
  effect: Effect<any, any>; // Effect a disparar (add ou edit)
  title: string;
  confirmText: string;
  cancelText: string;
  TriggerComponent: React.ReactElement<any>;
  isEdit?: boolean; // Flag pra ajustar payload se precisar
}

const CustomerFormModal: React.FC<Props> = ({
  customer,
  effect,
  title,
  confirmText,
  cancelText,
  TriggerComponent,
  isEdit = false,
}) => {
  const documentFieldRef = useRef<DocumentInputRef>(null);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState<string>(customer?.name ?? "");
  const [document, setDocument] = useState<string>(customer?.document ?? "");
  const [balance, setBalance] = useState<number>(customer?.balance ?? 0);

  useEffect(() => {
    if (customer && open) {
      setName(customer.name);
      setDocument(customer.document);
      setBalance(customer.balance ?? 0);
    }
  }, [customer, open]);

  useEffect(() => {
    if (!open) return;

    let unsubscribeFail: (() => void) | undefined;
    let unsubscribeDone: (() => void) | undefined;

    if (effect) {
      unsubscribeDone = effect.doneData.watch(() => {
        if (isEdit) {
          toast.success(`Sucesso ao editar cliente!`);
        } else {
          toast.success(`Sucesso ao cadastrar cliente!`);
        }
        if (!isEdit) handleClear();
        setOpen(false);
      });
      unsubscribeFail = effect.fail.watch(({ error }: any) => {
        if (error?.code === 409) {
          toast.error("Um cliente com este CNPJ já existe!");
        } else {
          if (isEdit) {
            toast.error(`Erro ao editar cliente!`);
          } else {
            toast.error(`Erro ao cadastrar cliente!`);
          }
        }
      });
    }

    return () => {
      if (unsubscribeFail) unsubscribeFail();
      if (unsubscribeDone) unsubscribeDone();
    };
  }, [effect, isEdit, open]);

  const handleClear = () => {
    setName("");
    setDocument("");
    setBalance(0);
  };

  const handleSubmit = async () => {
    if (!name || !document) {
      toast.error("Preencha todos os campos!");
      return;
    }

    if (documentFieldRef.current?.hasError()) {
      toast.error("Corrija o CNPJ!");
      return;
    }

    const payload = {
      ...(isEdit && customer?.id ? { id: customer.id } : {}),
      name,
      document,
      balance,
    };

    effect(payload);
  };

  const modalBody = (
    <Container>
      <TextField
        fullWidth
        label="Nome"
        variant="outlined"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Row>
        <DocumentField
          ref={documentFieldRef}
          document={document}
          onChange={setDocument}
        />
        <BalanceField value={balance} onChange={setBalance} />
      </Row>
    </Container>
  );

  return (
    <SimpleModal
      open={open}
      setOpen={setOpen}
      Component={TriggerComponent}
      onClose={!isEdit ? handleClear : undefined}
      onConfirm={handleSubmit}
      title={title}
      confirmText={confirmText}
      cancelText={cancelText}
      body={modalBody}
      maxWidth="md"
    />
  );
};

export default CustomerFormModal;
